namespace distill.dashboards.dataManager {
    
    const DEBOUNCE_DELAY_MS = 750;
    
    const _requestQueue: Set<string> = new Set();
    let _debounceTimer: number | null = null;
    let _dataFetchUrl: string = appn.emptyString;
    let _getToken: (() => Promise<string | null>) | null = null;

    export function init(dataFetchUrl: string, getToken?: () => Promise<string | null>): void {
        _dataFetchUrl = dataFetchUrl;
        _getToken = getToken || null;
    }
    
    export function bindEvents(){
        eventBus.subscribe(appn.designerv2.EVENTS.DATA_REFRESH_REQUESTED, (payload: { componentId: string }) => {
            if (!payload || !payload.componentId)
                return;

            _requestQueue.add(payload.componentId);
            if (_debounceTimer)
                clearTimeout(_debounceTimer);

            _debounceTimer = window.setTimeout(processQueue, DEBOUNCE_DELAY_MS);
        });

        eventBus.subscribeToPropertyUpdates(payload => {
            const component = designer.getComponent(payload.componentId);
            if (!component)
                return;

            const definition = componentManager.getDefinition(component.type);
            if (!definition)
                return;

            const dataQueryPropNames = componentManager.getDataQueryPropertyNames(definition);
            if (dataQueryPropNames.includes(payload.propName))
                eventBus.publish(EVENTS.DATA_REFRESH_REQUESTED, { componentId: payload.componentId });
        });
    }

    export function getCachedResult(cacheKey: string): IDataQueryResult | null {
        return dataCache.get(appn.utils.normalizeIdentifier(cacheKey)) || null;
    }

    // Server-paged table widgets: the fetch window (row offset + size) each table's next
    // refresh should request. The widget updates it on pager navigation; sort/filter commits
    // reset it to the first window, and the pane's commit resets every table's window.
    const TABLE_WINDOW_SIZE = 500;
    const _tableWindows = new Map<string, number>();

    export function getTableWindowSize(): number {
        return TABLE_WINDOW_SIZE;
    }

    export function getTableWindowOffset(componentId: string): number {
        return _tableWindows.get(componentId) || 0;
    }

    export function hasTableWindow(componentId: string): boolean {
        return _tableWindows.has(componentId);
    }

    export function setTableWindowOffset(componentId: string, offset: number): void {
        _tableWindows.set(componentId, Math.max(0, offset));
    }

    export function resetTableWindows(): void {
        // Keep every table registered (so the next full refresh still fetches windows with a
        // total count) but restart each from its first window.
        Array.from(_tableWindows.keys()).forEach(componentId => _tableWindows.set(componentId, 0));
    }

    export function getTableWindows(): { componentId: string, offset: number, limit: number }[] {
        return Array.from(_tableWindows.keys()).map(componentId => ({
            componentId: componentId,
            offset: _tableWindows.get(componentId) || 0,
            limit: TABLE_WINDOW_SIZE
        }));
    }

    /// Fetches a component's full filtered result for CSV export (one capped server-side
    /// window), independent of the paging buffer the table is currently displaying.
    export async function fetchFilterOptions(
        component: IDashboardComponent,
        column: string,
        filters: IDashFilterSelection[],
        search: string = '',
        values: string[] | null = null
    ): Promise<{ options: { value: string, count: number }[], available: { value: string, count: number }[] | null } | null> {
        const token = _getToken ? await _getToken() : null;
        if (!token)
            return null;

        try {
            // The unified filter-options endpoint, widget mode (component + column). The pane's
            // dropdown options use the same endpoint in filter mode (filterId). When `values` is
            // sent, the response also carries `available` — which of those candidates still
            // exist under the applied global filters (availability check).
            const optionsUrl = _dataFetchUrl.replace('/dash/data', '/dash/filter-options');
            const response = await fetch(optionsUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    accessToken: token,
                    componentId: component.id,
                    column: column,
                    filters: filters,
                    search: search,
                    values: values || undefined
                })
            });
            if (!response.ok)
                return null;
            const result = await response.json();
            if (!result || !result.Options)
                return { options: [], available: null };
            return {
                options: result.Options.map((o: { Value: string; Count?: number }) => ({ value: o.Value, count: o.Count || 0 })),
                available: result.Available ? result.Available.map((o: { Value: string; Count?: number }) => ({ value: o.Value, count: o.Count || 0 })) : null
            };
        } catch (err) {
            console.error('Error fetching filter options for ' + column, err);
            return null;
        }
    }

    function isDataQuerySufficient(componentType: ComponentType, dataQuery: IDataQuery): boolean {
        if (!dataQuery || !dataQuery.primaryDataset?.id)
            return false;

        const hasGroupBy = !!dataQuery.aggregation?.groupBy?.columnId;
        const hasMetrics = !!(dataQuery.aggregation?.metrics && dataQuery.aggregation.metrics.length > 0 && (dataQuery.aggregation.metrics[0].columnId || dataQuery.aggregation.metrics[0].function === 'Count'));
        const hasValidAggregation = hasGroupBy && hasMetrics;
        const hasValidProjection = !!(dataQuery.projection?.columns && dataQuery.projection.columns.length > 0 && dataQuery.projection.columns[0].columnId);

        if (componentType == ComponentType.table && hasGroupBy)
            return true;
        
        return hasValidAggregation || hasValidProjection;
    }

    async function processQueue(): Promise<void> {
        if (_requestQueue.size === 0)
            return;

        const componentIds = Array.from(_requestQueue);
        _requestQueue.clear();

        const dataQueriesForRequest: IDataQuery[] = [];
        for (const id of componentIds) {
            const component = designer.getComponent(id);
            if (!component)
                continue;

            const definition = componentManager.getDefinition(component.type);
            if (!definition)
                continue;

            for (const propName in definition.properties) {
                const propDef = definition.properties[propName];
                if (propDef.type === 'datasetSelector' && propDef.path) {
                    const dataQueryPropName = propDef.path.split('.')[0];
                    const dataQuery = (component as any)[dataQueryPropName] as IDataQuery | undefined;
                    if (dataQuery && isDataQuerySufficient(component.type as ComponentType, dataQuery)) {
                        const dataQueryForRequest = appn.utils.deepClone(dataQuery);
                        dataQueryForRequest.name = `${id}.${dataQueryPropName}`;
                        // Tables page server-side in the public view (no designer Row Limit) —
                        // keep the designer preview fetch bounded at the same default.
                        if ((component.type as ComponentType) === ComponentType.table && !dataQueryForRequest.limit)
                            dataQueryForRequest.limit = TABLE_WINDOW_SIZE;
                        dataQueriesForRequest.push(dataQueryForRequest);
                    }
                }
            }
        }

        if (dataQueriesForRequest.length === 0)
            return;

        console.log(`Fetching data for ${dataQueriesForRequest.map(d => d.name).join(', ')}`, dataQueriesForRequest);
        try {
            const response = await fetch(_dataFetchUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ dashDataQueries: dataQueriesForRequest })
            });

            if (!response.ok)
                throw new Error(`HTTP error! status: ${response.status}`);

            const result: IDataQueryResponse = await response.json();
            if (result.Error)
                throw new Error(`Query result error! Message: ${result.Error}`);


            console.log(`Response for ${dataQueriesForRequest.map(d => d.name).join(', ')} received`, result);
            eventBus.publish(EVENTS.DATA_FETCH_SUCCESS, result);

        } catch (error) {
            console.error("Failed to fetch dashboard data:", error);
        }
    }
}
